/* @flow */
import * as React from 'react';
import type { TranslatorProps } from 'react-i18next';
import { translate } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { Chip, FormControl, List } from '@material-ui/core';
import ElectroTextField from 'forms/components/inputs/ElectroTextField/ElectroTextField';
import Loading from 'components/Loading/Loading';
import WelcomePageStore from 'stores/WelcomePageStore';
import electroSelectStyles from '../ElectroSelect/ElectroSelect.module.scss';
import styles from '../ElectroSelectExtra/ElectroSelectExtra.module.scss';

type SearchBy = {
  key: string,
  label: string,
  active: boolean,
};

type Config = {
  key: string,
  url: string,
  renderKeys: Array,
  searchBy: SearchBy[],
};

type TypeProps = {|
  field: Object,
  config: Config,
|};
type InjectedProps = {
  welcomePageStore: WelcomePageStore,
} & TranslatorProps;

@translate()
@inject('welcomePageStore')
@observer
export class ElectroSearchDropdown extends ComponentWithInjectedProps<
  TypeProps,
  {},
  InjectedProps
> {
  @observable isFocused: boolean = false;
  @observable selectedItem = {};
  @observable selectedSearchKeys: SearchBy[] = [];
  @observable searchText = '';

  componentDidMount() {
    const { config } = this.props;

    this.selectedSearchKeys = config.searchBy;
  }

  handleSearch(evt) {
    this.searchText = evt.target.value;

    this.search(this.searchText);
  }

  search(value: string) {
    const { welcomePageStore, config } = this.props;
    const searchBy = this.buildSearch(value);

    welcomePageStore.loadSearchResults(value, searchBy, config);
  }

  buildSearch(value: string): string {
    const activeSearch = this.selectedSearchKeys.filter(
      (searchBy: SearchBy) => searchBy.active === true
    );
    const searchBy = activeSearch.map((searchBy: SearchBy) => {
      return `${searchBy.key}:${value}`;
    });

    return searchBy.join(';');
  }

  renderItem(item): string {
    const { config } = this.props;

    return config.renderKeys
      .reduce((keyArr, key) => {
        keyArr.push(item[key]);
        return keyArr;
      }, [])
      .join(' - ');
  }

  setSelectedItem(index: boolean) {
    const { welcomePageStore } = this.props;

    welcomePageStore.selectedItem = index;
  }

  async toggleSearchKeyChip(item: SearchBy) {
    this.selectedSearchKeys = this.selectedSearchKeys.map((searchBy: SearchBy) => {
      return {
        ...searchBy,
        active: item.key === searchBy.key,
      };
    });

    await this.search(this.searchText);
  }

  render() {
    const { field, welcomePageStore, t } = this.props;
    const { searchResults, noSearchResults, loading, selectedItem } = welcomePageStore;
    const isFocusedClass = this.isFocused ? electroSelectStyles['is-focused'] : '';
    const isErrorClass = field.error ? electroSelectStyles['is-error'] : '';

    return (
      <div className={`${styles['select-wrapper']}`}>
        <div
          className={`${electroSelectStyles['electro-select']} ${isFocusedClass} ${isErrorClass}`}
        >
          <svg className={electroSelectStyles['search-icon-text-field']}>
            <use xlinkHref="#icon-search" />
          </svg>
          <FormControl>
            <ElectroTextField field={field} onKeyUp={event => this.handleSearch(event)} />
            <div className={styles['select-chips']}>
              <div className={styles['select-chips-label']}>Search By:</div>
              <div className={styles['select-chips-wrapper']}>
                {this.selectedSearchKeys.map((item: SearchBy, index) => {
                  const colour = item.active ? 'primary' : 'secondary';
                  return (
                    <Chip
                      key={item.key}
                      label={t(item.label)}
                      color={colour}
                      onClick={() => this.toggleSearchKeyChip(item, index)}
                    />
                  );
                })}
              </div>
            </div>
            {searchResults.length > 0 && (
              <div className={styles['dropdown-content']}>
                <List className={styles['dropdown-items']}>
                  {!loading &&
                    searchResults.map((item, index) => {
                      const activeClass = item.id === selectedItem.id ? styles['active'] : '';
                      return (
                        <li
                          key={item.id}
                          className={`${electroSelectStyles['search-text-field-item']} ${
                            styles['dropdown-item']
                          } ${activeClass}`}
                          onClick={() => this.setSelectedItem(index)}
                        >
                          <div>
                            <svg>
                              <use xlinkHref="#icon-assets" />
                            </svg>
                          </div>
                          <h3>{this.renderItem(item)}</h3>
                        </li>
                      );
                    })}
                </List>
              </div>
            )}
            {noSearchResults && (
              <div className={styles['no-search-results']}>
                {t('general:search_results.no_results_found')}
              </div>
            )}
            {loading && (
              <div className={electroSelectStyles['text-field-loader']}>
                <Loading variant={'small'} />
              </div>
            )}
          </FormControl>
        </div>
      </div>
    );
  }
}
